import { fetchIntegrations, fetchMobileApps } from 'modules/mobileApps/actions';
import { APPS_AND_WEBSITES_BREADCRUMB } from 'modules/mobileApps/constants';
import {
  IntegrationMetadataProvider,
  useIntegrationMetadataContext,
} from 'modules/mobileApps/context/integrationMetadata';
import { ModalProvider, ModalType, useModalContext } from 'modules/mobileApps/context/modal';
import {
  STATE,
  getIsFetchingMobileApps,
  getMobileAppById,
  getMobileAppIntegrationsById,
  selectAreAllIntegrationSlotsAssigned,
  selectFilteredUnassignedIntegrations,
} from 'modules/mobileApps/selectors';
import { AppPlatform } from 'modules/mobileApps/types';
import { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { Annotation } from 'ui-beta/annotatedSection';
import { BUTTON_SIZES, BUTTON_VARIANTS, IconButton } from 'ui/button';
import { Delete16, Edit16 } from 'ui/icons';
import { LinkButton } from 'ui/linkButton';
import { PageHeader } from 'ui/pageHeader';
import { Tooltip } from 'ui/tooltip';
import { isEmpty } from '@iterable/utils';
import { EmptyState, UnassignedIntegrations } from '..';
import { AppIcon } from '../AppIcon';
import { Modals } from '../modals';
import {
  ActionButtonsContainer,
  SectionHeader,
  SectionTitle,
  StyledBox,
  StyledDL,
  StyledDetailsHeader,
  StyledHeaderTitle,
  StyledSection,
  StyledSectionContent,
} from './AppDetails.styled';
import { AssignedIntegrations } from './AssignedIntegrations';

const withContext = (Component: FC) => () => (
  <IntegrationMetadataProvider>
    <ModalProvider>
      <Component />
    </ModalProvider>
  </IntegrationMetadataProvider>
);

export const AppDetails = withContext(() => {
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();

  const mobileApp = useSelector((state: STATE) => getMobileAppById(state, { id }));
  const assignedIntegrations = useSelector((state: STATE) =>
    getMobileAppIntegrationsById(state, { id }),
  );
  const isFetchingApp = useSelector(getIsFetchingMobileApps);
  const unassignedInts = useSelector((state: STATE) =>
    selectFilteredUnassignedIntegrations(state, { id }),
  );
  const areAllIntsAssigned = useSelector((state: STATE) =>
    selectAreAllIntegrationSlotsAssigned(state, { id }),
  );

  const { setAppDetails, setApps, setAssignedIntegrations } = useIntegrationMetadataContext();
  const { openModal } = useModalContext();

  const { name, platform, storeUrl, packageName } = mobileApp;
  const isMobilePlatform = platform === AppPlatform.ANDROID || platform === AppPlatform.IOS;
  const showUnassignedInts = !areAllIntsAssigned && unassignedInts.length > 0 && isMobilePlatform;

  useEffect(() => {
    if (Object.hasOwn(mobileApp ?? {}, 'id')) {
      setAppDetails(mobileApp);
      setApps([mobileApp]);
    }
  }, [mobileApp]);

  useEffect(() => {
    setAssignedIntegrations(assignedIntegrations ?? {});
  }, [assignedIntegrations]);

  useEffect(() => {
    dispatch(fetchIntegrations());
    dispatch(fetchMobileApps());
  }, []);

  return isFetchingApp || !mobileApp.id ? (
    <EmptyState isLoading={isFetchingApp}>This app does not exist</EmptyState>
  ) : (
    <>
      <PageHeader breadcrumb={APPS_AND_WEBSITES_BREADCRUMB}>
        <StyledHeaderTitle>
          <AppIcon platformType={platform} width={24} />
          {name}
        </StyledHeaderTitle>
      </PageHeader>
      <StyledSection>
        <SectionHeader>
          <SectionTitle>Details</SectionTitle>
          Manage your app&#39;s name and configuration
        </SectionHeader>
        <StyledSectionContent>
          <StyledBox id="details-box">
            <StyledDetailsHeader>
              <ActionButtonsContainer>
                <LinkButton
                  size={BUTTON_SIZES.SMALL}
                  variant={BUTTON_VARIANTS.SECONDARY}
                  to={`/integrations/mobileApps/${id}/edit`}
                >
                  <Edit16 />
                  Edit Details
                </LinkButton>
                <Tooltip
                  isEnabled={!isEmpty(assignedIntegrations)}
                  tooltipContent="Contact your CSMs to delete this app"
                >
                  <IconButton
                    data-test="mobileApps-details-delete-button"
                    isDisabled={!isEmpty(assignedIntegrations)}
                    onClick={() => openModal(ModalType.DeleteAppModal)}
                    size={BUTTON_SIZES.SMALL}
                    style={{ width: 'unset' }}
                    variant={BUTTON_VARIANTS.SECONDARYDESTRUCTIVE}
                  >
                    <Delete16 />
                  </IconButton>
                </Tooltip>
              </ActionButtonsContainer>
            </StyledDetailsHeader>
            <StyledDL>
              <dt>App Name</dt>
              <dd>{name}</dd>
              <dt>Platform</dt>
              <dd>{platform}</dd>
              <dt>Package Name</dt>
              <dd>{packageName}</dd>
              {!!storeUrl && <dt>Store URL</dt>}
              {!!storeUrl && <dd>{storeUrl}</dd>}
            </StyledDL>
          </StyledBox>
        </StyledSectionContent>
      </StyledSection>
      {isMobilePlatform && <AssignedIntegrations />}
      {showUnassignedInts && (
        <StyledSection>
          <Annotation title="Unassigned Integrations">
            Use an existing integration by assigning it to the app.
          </Annotation>
          <StyledSectionContent>
            <StyledBox id="mobileApps-unassignedIntegrations">
              <UnassignedIntegrations integrations={unassignedInts} showHeader={false} />
            </StyledBox>
          </StyledSectionContent>
        </StyledSection>
      )}
      <Modals />
    </>
  );
});
